/**
 * Параметры GET /tickets/ из query string (как `ticketsParamsFromSearch` в TicketsPage).
 */
import { excludeResolvedClosedFromSearchParams } from '@/lib/tickets-list-filters-url'
import {
  TICKETS_AI_DEFAULT_SORT_ORDER,
  TICKETS_DEFAULT_SORT_BY,
  TICKETS_DEFAULT_SORT_ORDER,
  parseTicketsSortOrder,
} from '@/lib/tickets-list-sort'
import type { WorkspaceTicketsSortDefaults } from '@/lib/workspace-tickets-sort-defaults'

export function ticketsParamsFromSearch(
  searchParams: URLSearchParams,
  options: {
    aiRoute: boolean
    sortDefaults?: WorkspaceTicketsSortDefaults
  },
): Record<string, string | number | boolean | undefined> {
  const workspaceDefaults = options.sortDefaults
  const defaultSortBy = workspaceDefaults?.sortBy ?? TICKETS_DEFAULT_SORT_BY
  const defaultSortOrder =
    options.aiRoute && !searchParams.get('sort_order') && defaultSortBy === 'updated_at'
      ? TICKETS_AI_DEFAULT_SORT_ORDER
      : (workspaceDefaults?.sortOrder ?? TICKETS_DEFAULT_SORT_ORDER)
  const o: Record<string, string | number | boolean | undefined> = {
    sort_by: searchParams.get('sort_by') || defaultSortBy,
    sort_order: parseTicketsSortOrder(searchParams.get('sort_order') || defaultSortOrder),
  }

  const project = searchParams.get('project')
  if (project) o.project = project

  const projectIdParam = searchParams.get('project_id')?.trim()
  if (projectIdParam) o.project_id = projectIdParam

  const statusRaw = (searchParams.get('status') || '').trim()
  if (statusRaw) o.status = statusRaw
  const statusSegments = statusRaw
    ? statusRaw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    : []
  const urlMentionsEmergency = statusSegments.includes('emergency')

  const priority = searchParams.get('priority')
  if (priority) o.priority = priority
  const channelType = (searchParams.get('channel_type') || '').trim()
  if (channelType) o.channel_type = channelType

  const assignedToMeParam = searchParams.get('assigned_to_me') === 'true'
  if (assignedToMeParam) {
    o.assigned_to_me = true
  } else {
    const assignedTo = searchParams.get('assigned_to_id')
    if (assignedTo) o.assigned_to_id = assignedTo
  }

  const assigned = searchParams.get('assigned')
  if (!assignedToMeParam && assigned === 'false') o.assigned = false
  if (!assignedToMeParam && assigned === 'true') o.assigned = true
  if (searchParams.get('last_message_not_me') === 'true') o.last_message_not_me = true

  const cdf = searchParams.get('created_date_from')?.trim()
  const cdt = searchParams.get('created_date_to')?.trim()
  if (cdf && cdt && /^\d{4}-\d{2}-\d{2}$/.test(cdf) && /^\d{4}-\d{2}-\d{2}$/.test(cdt)) {
    o.created_date_from = cdf
    o.created_date_to = cdt
  } else {
    const cwh = searchParams.get('created_within_hours')
    if (cwh) {
      const n = parseInt(cwh, 10)
      if (!Number.isNaN(n) && n >= 1) o.created_within_hours = n
    }
  }

  const uwh = searchParams.get('updated_within_hours')
  if (uwh) {
    const n = parseInt(uwh, 10)
    if (!Number.isNaN(n) && n >= 1) o.updated_within_hours = n
  }

  const tagIds = searchParams.get('tag_ids')
  if (tagIds) o.tag_ids = tagIds

  const qSearch = searchParams.get('search')?.trim()
  if (qSearch) o.search = qSearch

  o.exclude_resolved_closed = excludeResolvedClosedFromSearchParams(searchParams)

  if (searchParams.get('urgent') === 'true') {
    o.priority = 'urgent'
    o.status = 'new,open,in_progress,pending'
  }

  // По умолчанию бэкенд exclude_emergency=true — аварийные только во вкладке «Авария».
  // В общий список добавляем их только по чекбоксу «Аварии» или фильтру status=emergency.
  if (searchParams.get('include_emergency') === 'true') {
    o.exclude_emergency = false
  }
  if (urlMentionsEmergency) {
    o.exclude_emergency = false
  }

  if (options.aiRoute) {
    if (!assignedToMeParam) o.assigned_to_ai = true
    if (searchParams.get('last_message_not_ai') === 'true') {
      o.last_message_not_ai = true
    }
    if (searchParams.get('last_message_not_system') === 'true') {
      o.last_message_not_system = true
    }
  } else if (!assignedToMeParam) {
    o.exclude_ai_assigned = true
  }

  return o
}
